import ELK from 'elkjs/lib/elk.bundled.js';
import { delegateHeight, delegateWidth, externalNodeHeight, externalNodeWidth, gapBTWLeftBorderAndExternalNode, internalSystem } from '../../config/common';

class LayoutService {
    constructor(layoutEngine = null) {
        this.engine = layoutEngine || new ELK();
    }

    /**
     * Layout React Flow nodes + edges
     */
    async layout(nodes, edges, options = {}) {
        const graph = this._prepareGraph(nodes, edges, options, internalSystem);
        const layoutedGraph = await this.engine.layout(graph);
        return this._mapToReactFlow(layoutedGraph, nodes);
    }

    /**
     * Convert React Flow nodes/edges → ELK graph
     */
    _prepareGraph(nodes, edges, options, internalSystem) {
        const elkNodes = [];
        const elkEdges = edges.map((e) => ({
            id: e.id,
            sources: [e.source],
            targets: [e.target],
        }));


        const groupLayoutOptions = {
            "elk.algorithm": "force",
            "elk.hierarchyHandling": "INCLUDE_CHILDREN",
            "elk.force.iterations": 1000,
            "elk.force.repulsivePower": 15,
            "elk.force.repulsion": 5000,
            "elk.force.idealEdgeLength": 250,
            "elk.force.edgeSpringStrength": 0.2,
            "elk.force.boundingBox": "PARENT",        // <--- Keep children inside parents
            "elk.spacing.nodeNode": 150,
            "elk.padding": "[top=100,left=100,bottom=100,right=100]"
          }

        const internalSystemHeight = internalSystem.style?.height;
        const internalSystemWidth = internalSystem.style?.width;
        // Group nodes into ELK format
        nodes.forEach((n) => {
            if (n.type === 'group') {
                elkNodes.push({
                    id: internalSystem.id,
                    width: internalSystemWidth,
                    height: internalSystemHeight,
                    layoutOptions: groupLayoutOptions,
                    children: nodes
                        .filter((c) => c.parentNode === n.id)
                        .map((c) => ({
                            id: c.id,
                            width: c.style?.width || 150,
                            height: c.style?.height || 50,
                        })),
                });
            }else if (!n.parentNode && !["sensor", "user", "external_function"].includes(n.type)) {
                // any remaining external nodes not in external_group
                elkNodes.push({
                    id: n.id,
                    width: n.style?.width || 150,
                    height: n.style?.height || 50
                });
            }
        });
        const externalNodes = nodes.filter(n => !n.parentNode && ["sensor", "user", "external_function"].includes(n.type))
                          .map(n => ({
                              id: n.id,
                              width: n.style?.width || 150,
                              height: n.style?.height || 50
                          }));

        // 2️⃣ Push fake external group
        if (externalNodes.length > 0) {
            elkNodes.push({
                id: "external_group",
                width: 200, // width of the group to push nodes to the left
                height: internalSystem.style.height, // height to cover system model
                y: internalSystem.position.y,
                layoutOptions: {
                    "elk.algorithm": "layered",
                    "elk.direction": "DOWN",
                    "elk.layered.spacing.nodeNodeBetweenLayers": 50,
                    "elk.spacing.nodeNode": 50,
                    "elk.padding": "[top=200,left=50,bottom=50,right=50]"
                },
                children: externalNodes
            });
        }
        
        const rootLayoutOptions = {
            "elk.algorithm": "fixed",
        };

        return {
            id: 'root',
            children: elkNodes,
            edges: elkEdges,
            layoutOptions: rootLayoutOptions,
        };
    }

    /**
     * Map ELK output → React Flow nodes
     * Includes custom placement rules for sensors/delegates
     */
    _mapToReactFlow(layoutedGraph, originalNodes) {
        const positions = {};
        const dimensions = {}

        // Recursively gather positions
        const traverse = (node) => {
            if (node.x !== undefined && node.y !== undefined) {
                positions[node.id] = {x: node.x, y: node.y};
                dimensions[node.id] = {width: node.width, height: node.height}
            }
            if (node.children) node.children.forEach(traverse);
        };

        layoutedGraph.children.forEach(traverse);
        let delegateCount = 0;
        // Apply positions + custom rules
        return originalNodes.map((n) => {
            let pos = positions[n.id] || n.position || {x: 0, y: 0};
            let width = n.style.width;
            let height = n.style.height;

            // Apply special placement rules
            if (n.type === 'sensor' || n.type === 'user') {
                // Snap to left side of parent
                const parent = originalNodes.find((p) => p.id === 'A');
                if (parent) {
                    pos = {
                        x: parent.position.x - gapBTWLeftBorderAndExternalNode - externalNodeWidth,
                        y: pos.y,
                    };
                }
            } else if (n.type === 'external_function') {
                const parent = originalNodes.find((p) => p.id === 'A');
                if (parent) {
                    pos = {
                        x: parent.position.x - (delegateWidth / 2),
                        y: parent.position.y + (delegateCount * delegateHeight) + ((delegateCount + 1) * 35),
                    };
                }
                delegateCount++;
                n.type = 'external_function';
            } else if (n.type === 'controller_delegate') {
                const parent = originalNodes.find((p) => p.id === 'A');
                if (parent) {
                    pos = {
                        x: parent.position.x + parent.style.width / 2 - 100,
                        y: parent.position.y + 14,
                    };
                }
            } else if (n.id === 'A') {
                const node = originalNodes.find((p) => p.id === 'A');
                if (node) {
                    pos = node.position;
                    width = Math.max(dimensions[n.id].width, internalSystem.style.width);
                    height = Math.max(dimensions[n.id].height, internalSystem.style.height);
                }
            }else if(n.id === "labelNode"){
                const parent = originalNodes.find((p) => p.id === 'A');
                if (parent) {
                    pos = {
                        x: parent.style.width - 120,
                        y: 10,
                    };
                }
            }

            const node = {
                ...n,
                position: pos,
                style: {...n.style, width, height}
            }

            if(n.id === "A" || n.type === "controller_delegate"){
                node.draggable = false;
            }

            return node
        });
    }

    /**
     * Swap layout engine (future proof)
     */
    setEngine(engine) {
        this.engine = engine;
    }
}

export default LayoutService;
